import { ReactNode } from 'react';
import { Link as RouterLink, useParams } from 'react-router';
import {
    Alert,
    Box,
    Button,
    Chip,
    Container,
    Paper,
    Stack,
    Typography,
} from '@mui/material';

import { Icon } from '../shared/Icon';
import { Loading } from '../shared';

import { useEmailAuditItem } from '../hooks/emailAudit';

import { RouteTo } from '../routes';

import { STATUS_COLORS } from './format';

const MONO_SX = { fontFamily: 'monospace', fontSize: 13 };

function Field({ label, children }: { label: string; children: ReactNode }) {
    return (
        <Box sx={{ minWidth: 0 }}>
            <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 600, textTransform: 'uppercase' }}
            >
                {label}
            </Typography>
            <Typography
                component="div"
                variant="body2"
                sx={{ overflowWrap: 'anywhere' }}
            >
                {children}
            </Typography>
        </Box>
    );
}

function BackButton() {
    return (
        <Button
            component={RouterLink}
            to={RouteTo.EmailAuditList()}
            size="small"
            startIcon={<Icon name="arrow_back" />}
            sx={{ alignSelf: 'flex-start' }}
        >
            Back to email audit list
        </Button>
    );
}

export function EmailAuditItem() {
    const { id } = useParams<{ id: string }>();
    const { emailAuditItem: audit, isLoading, error } = useEmailAuditItem(id);

    if (isLoading) {
        return <Loading />;
    }

    if (error || !audit) {
        return (
            <Container>
                <Stack sx={{ gap: 2, my: 2 }}>
                    <BackButton />
                    <Alert severity="error">
                        Unable to load this email audit.
                    </Alert>
                </Stack>
            </Container>
        );
    }

    return (
        <Container>
            <Stack sx={{ gap: 2, my: 2 }}>
                <BackButton />
                <Paper
                    sx={{
                        p: 2,
                        display: 'grid',
                        gridTemplateColumns:
                            'repeat(auto-fit, minmax(220px, 1fr))',
                        gap: 2,
                    }}
                >
                    <Field label="Status">
                        <Chip
                            size="small"
                            color={
                                STATUS_COLORS[audit.deliveryStatus] ?? 'default'
                            }
                            label={audit.deliveryStatus}
                        />
                    </Field>
                    <Field label="Subject">{audit.subject}</Field>
                    <Field label="To">{audit.recipientEmail}</Field>
                    <Field label="Template Type">{audit.emailTemplate}</Field>
                    <Field label="Order ID">
                        <Box component="span" sx={MONO_SX}>
                            {audit.orderId}
                        </Box>
                    </Field>
                    <Field label="Message ID">
                        <Box component="span" sx={MONO_SX}>
                            {audit.postmarkMessageId}
                        </Box>
                    </Field>
                    <Field label="Created At">{audit.createdAt}</Field>
                    {audit.deliveredAt && (
                        <Field label="Delivered At">{audit.deliveredAt}</Field>
                    )}
                    <Field label="Bounced At">{audit.bouncedAt ?? '-'}</Field>
                </Paper>
                <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                    <Box
                        component="iframe"
                        title={`Email body: ${audit.subject}`}
                        sandbox=""
                        srcDoc={audit.htmlBody}
                        sx={{
                            display: 'block',
                            width: '100%',
                            height: 600,
                            border: 0,
                            bgcolor: 'common.white',
                        }}
                    />
                </Paper>
            </Stack>
        </Container>
    );
}
